import { PermissionContext } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { COMPANY_SCOPABLE_PERMISSIONS } from './roles-and-permissions.js';

/**
 * Check if a company is in scope for any scopable permission.
 */
export function hasCompanyInScope(scope: PermissionContext[] | undefined, companyId: string): boolean {
  if (!scope) return false;
  return scope.some(
    ctx =>
      (COMPANY_SCOPABLE_PERMISSIONS as readonly string[]).includes(ctx.permission) && ctx.contexts?.includes(companyId),
  );
}

/** Company IDs from {@link COMPANY_SCOPABLE_PERMISSIONS} rows only (ignores Nexus client scopes, etc.). */
export function getCompanyIdsFromScope(scope: PermissionContext[] | undefined): string[] {
  if (!scope) return [];
  const allIds = scope.flatMap(ctx => ctx.contexts ?? []);
  return [...new Set(allIds)];
}

/**
 * Add a company to all scopable permissions.
 */
export function addCompanyToScope(scope: PermissionContext[] | undefined, companyId: string): PermissionContext[] {
  const result = [...(scope ?? [])];
  for (const permission of COMPANY_SCOPABLE_PERMISSIONS) {
    const existing = result.find(ctx => ctx.permission === permission);
    if (existing) {
      if (!existing.contexts?.includes(companyId)) {
        existing.contexts = [...(existing.contexts ?? []), companyId];
      }
    } else {
      result.push(new PermissionContext({ permission, contexts: [companyId] }));
    }
  }
  return result;
}

/**
 * Remove a company from all scopable permissions.
 */
export function removeCompanyFromScope(scope: PermissionContext[] | undefined, companyId: string): PermissionContext[] {
  if (!scope) return [];
  return scope
    .map(ctx => {
      if ((COMPANY_SCOPABLE_PERMISSIONS as readonly string[]).includes(ctx.permission)) {
        const remaining = (ctx.contexts ?? []).filter(id => id !== companyId);
        if (remaining.length === 0) return null;
        // NOTE: ...ctx does NOT work on AdornisEntity - fields are non-enumerable (Object.defineProperties)
        return new PermissionContext({ _id: ctx._id, permission: ctx.permission, contexts: remaining });
      }
      return ctx;
    })
    .filter((ctx): ctx is PermissionContext => ctx !== null);
}
